import { toast } from "react-toastify";
import {
  HUBSPOT_INLINE_CHAT_SELECTOR,
  SUPPORT_CHAT_OPEN_EVENT,
} from "./hubspot-inline-chat";
import type { SupportChatOpenDetail } from "./hubspot-inline-chat";

export type HubSpotHelpChatTopic =
  "generic" | "accounts" | "plans" | "approvals" | "onboarding" | "upsell";

// The narrowed type that `waitForHubSpotReady` guarantees before invoking
// its callback. Callers receive the confirmed API instance directly rather
// than re-reading `window.HubSpotConversations` (which would widen back to
// `| undefined` across the async boundary).
type ReadyHubSpotConversations = NonNullable<Window["HubSpotConversations"]>;

function waitForHubSpotReady(
  callback: (api: ReadyHubSpotConversations) => void,
  maxWaitTimeMs: number = 10_000
) {
  const startTime = Date.now();

  const checkReady = () => {
    try {
      const api = window.HubSpotConversations;
      // Check if HubSpot Conversations is ready
      if (api && api.widget && typeof api.widget.open === "function") {
        callback(api);
        return;
      }
    } catch (error) {
      console.debug("HubSpot not yet ready:", error);
    }

    if (Date.now() - startTime > maxWaitTimeMs) {
      console.error("HubSpot widget failed to load within timeout period");

      return;
    }

    // Try again in 100ms
    setTimeout(checkReady, 100);
  };

  checkReady();
}

function dispatchInlineSupportChatOpen(
  message: string,
  helpChatTopic: HubSpotHelpChatTopic
): boolean {
  if (!document.querySelector(HUBSPOT_INLINE_CHAT_SELECTOR)) {
    return false;
  }

  window.dispatchEvent(
    new CustomEvent<SupportChatOpenDetail>(SUPPORT_CHAT_OPEN_EVENT, {
      detail: { message, helpChatTopic },
    })
  );
  return true;
}

export function openHubSpotSupportWidget(
  message = "",
  helpChatTopic: HubSpotHelpChatTopic = "generic"
) {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const currentQuery = searchParams.get("hs-sked-chat");

    if (currentQuery === helpChatTopic) {
      waitForHubSpotReady((api) => {
        if (message) {
          api.widget.open({ message });
        } else {
          api.widget.open();
        }
      });
      return;
    }

    // otherwise update the url param, refresh hubspot and then open it
    window.history.pushState({}, "", `?hs-sked-chat=${helpChatTopic}`);

    waitForHubSpotReady((api) => {
      api.widget.refresh({ openToNewThread: true });

      // Wait for the refresh to complete before opening
      waitForHubSpotReady((api2) => {
        if (message) {
          api2.widget.open({ message });
        } else {
          api2.widget.open();
        }
      }, 5000);
    });
  } catch (error) {
    console.error("Error opening HubSpot chat:", error);
    toast.error(
      "There was a problem opening live chat (please check your ad blocker is disabled). Please try the Support menu again or email hello@skedsocial.com."
    );
  }
}

export function openSupportChat(
  message = "",
  helpChatTopic: HubSpotHelpChatTopic = "generic"
) {
  if (dispatchInlineSupportChatOpen(message, helpChatTopic)) {
    return;
  }

  openHubSpotSupportWidget(message, helpChatTopic);
}
